import '@sapudex/web-components/dist/Accordion.js';
import '@sapudex/web-components/dist/AccordionItem.js';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  OnInit,
  QueryList,
  ViewChildren
} from '@angular/core';
import {DcpTechnicalInformationData} from '@app/model/dcp-technical-information-data';
import {DcpComponentClassifications, JsonService} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {CmsComponent, TranslatePipe} from '@spartacus/core';
import {CmsComponentData, CurrentProductService} from '@spartacus/storefront';
import {DcpProductUtilitiesService} from '../../services/dcp-product-utilities.service';
import {ClassificationsTemplateComponent} from '../classifications-template/classifications-template.component';
import {NavigationLink} from '../navigation-bar/navigation-link';
import {ProductDetailsNavigationLinkService} from '../navigation-bar/product-details-navigation-link.service';
import {ProductDetailsSectionComponent} from '../product-details-section/product-details-section.component';
import {ProductPageDecoratorService} from '@app/product/services/product-page-decorator.service';

export interface ProductTechnicalInformationComponent extends CmsComponent {
  classificationsProductData?: DcpComponentClassifications[];
}

interface TechnicalSection {
  header: string;
  data: DcpTechnicalInformationData[];
}

@UntilDestroy()
@Component({
  selector: 'dcp-technical-information',
  templateUrl: './technical-information.component.html',
  styleUrls: ['./technical-information.component.scss'],
  imports: [ClassificationsTemplateComponent, TranslatePipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class TechnicalInformationComponent extends ProductDetailsSectionComponent implements OnInit, AfterViewInit {
  @ViewChildren(ClassificationsTemplateComponent) classificationsTemplate!: QueryList<ClassificationsTemplateComponent>;
  productClassifications!: DcpComponentClassifications[];
  sections: TechnicalSection[] = [];
  navLink: NavigationLink = {
    headline: 'productDetails.technicalInformation',
    id: 'technical-information-section',
  };

  constructor(
    dcpProductUtilitiesService: DcpProductUtilitiesService,
    protected currentProductService: CurrentProductService,
    protected changeDetection: ChangeDetectorRef,
    protected productDetailsNavigationLinkService: ProductDetailsNavigationLinkService,
    protected productPageDecoratorService: ProductPageDecoratorService,
    private cmsData: CmsComponentData<ProductTechnicalInformationComponent>,
    private jsonService: JsonService,
  ) {
    super(
      currentProductService,
      changeDetection,
      productDetailsNavigationLinkService,
      dcpProductUtilitiesService,
      productPageDecoratorService,
    );
  }

  get hasGeneral(): boolean {
    return !!(this.product?.classifications?.length || this.product?.supcategories?.length);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.cmsData.data$.pipe(untilDestroyed(this)).subscribe((componentData) => {
      if (componentData.classificationsProductData) {
        this.productClassifications = this.jsonService.parseSafely(
          componentData.classificationsProductData.toString(),
        ) as DcpComponentClassifications[];
      }
    });
  }

  ngAfterViewInit(): void {
    this.classificationsTemplate.changes.pipe(untilDestroyed(this)).subscribe(() => {
      this.changeDetection.detectChanges();
    });
  }

  isVisible(): boolean {
    return this.hasGeneral || this.sections.length > 0;
  }

  prepareData(): void {
    if (this.product?.data) {
      this.prepareTechnicalInformation();
    }
  }

  prepareTechnicalInformation(): void {
    const deployment: DcpTechnicalInformationData[] = [];
    const operations: DcpTechnicalInformationData[] = [];
    const security: DcpTechnicalInformationData[] = [];
    const compliance: DcpTechnicalInformationData[] = [];
    const globalization: DcpTechnicalInformationData[] = [];

    const raw = this.product?.data?.find((entry) => entry.type === 'selfDeclarations');
    if (!raw?.value) {
      return;
    }

    const parsed = this.jsonService.parseSafely(raw.value) as DcpTechnicalInformationData[];
    if (!parsed) {
      return;
    }

    const buckets: Record<string, DcpTechnicalInformationData[]> = {
      Deployment: deployment,
      Operations: operations,
      Security: security,
      Compliance: compliance,
      Globalization: globalization,
    };
    for (const item of parsed) {
      buckets[item.type.trim()]?.push(item);
    }

    this.sections = [
      {header: 'productDetails.deployment', data: deployment},
      {header: 'productDetails.operations', data: operations},
      {header: 'productDetails.security', data: security},
      {header: 'productDetails.compliance', data: compliance},
      {header: 'productDetails.globalization', data: globalization},
    ].filter((s) => s.data.length > 0);
  }

  half(x: number): number {
    return Math.ceil(x / 2);
  }
}
